import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertCircle,
	Download,
	FileText,
	Loader2,
	Paperclip,
	RotateCw,
	X,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import type { Attachment } from "@/shared/types/entries";
import { useApi } from "@/shared/utils/request";
import {
	ATTACHMENT_ALLOWED_TYPES,
	formatBytes,
	validateAttachmentFile,
} from "@/shared/validation/attachments";
import { Button } from "@/v2/components/ui/button";
import { Label } from "@/v2/components/ui/label";
import { actingHeaders } from "@/v2/hooks/actions/types";
import { useEntryAttachmentMutations } from "@/v2/hooks/useEntryAttachments";

type Upload = {
	id: string;
	file: File;
	status: "uploading" | "error";
	progress: number;
	error?: string;
};

function addAttachment(list: Attachment[], attachment: Attachment) {
	if (list.some((a) => a.id === attachment.id)) return list;
	return [...list, attachment];
}

export type AttachmentFieldProps = {
	/** Create mode (no entryId): stage files for the parent to upload after the
	 * entry is created. */
	files?: File[];
	onFilesChange?: (files: File[]) => void;
	/** Edit mode: uploads and deletes happen immediately against this entry. */
	entryId?: string;
	attachments?: Attachment[];
	timekeeperId?: string | null;
	/** View/download only (deleted or exported entries). */
	disabled?: boolean;
};

function Row({
	icon,
	name,
	meta,
	actions,
}: {
	icon: React.ReactNode;
	name: string;
	meta?: React.ReactNode;
	actions?: React.ReactNode;
}) {
	return (
		<div className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5">
			<span className="shrink-0 text-muted-foreground">{icon}</span>
			<p className="min-w-0 flex-1 truncate text-sm">{name}</p>
			{meta && (
				<span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground">
					{meta}
				</span>
			)}
			{actions && (
				<div className="flex shrink-0 items-center gap-0.5">{actions}</div>
			)}
		</div>
	);
}

export function AttachmentField({
	files,
	onFilesChange,
	entryId,
	attachments,
	timekeeperId,
	disabled = false,
}: AttachmentFieldProps) {
	const { makeApiRequest } = useApi();
	const { upload, remove } = useEntryAttachmentMutations(entryId);
	const queryClient = useQueryClient();
	const inputRef = React.useRef<HTMLInputElement>(null);
	const attachmentQueryKey = React.useMemo(
		() => ["entry-attachments", entryId, timekeeperId ?? "default"] as const,
		[entryId, timekeeperId],
	);
	const attachmentQuery = useQuery({
		queryKey: attachmentQueryKey,
		queryFn: async () => {
			const resp = await makeApiRequest({
				endpoint: `/v2/entries/${entryId}/attachments`,
				method: "GET",
				headers: actingHeaders(timekeeperId),
			});
			if (resp.status === "ERROR") {
				throw new Error(resp.error || "Failed to load attachments");
			}
			return (resp.data?.data ?? []) as Attachment[];
		},
		enabled: Boolean(entryId) && attachments === undefined,
		staleTime: 60 * 1000,
	});

	const [optimisticAttachments, setOptimisticAttachments] = React.useState<
		Attachment[]
	>([]);
	const [removedAttachmentIds, setRemovedAttachmentIds] = React.useState<
		ReadonlySet<string>
	>(() => new Set());
	const [uploads, setUploads] = React.useState<Upload[]>([]);

	const existing = React.useMemo(() => {
		const source = attachments ?? attachmentQuery.data ?? [];
		const visible = source.filter(
			(attachment) => !removedAttachmentIds.has(attachment.id),
		);
		return optimisticAttachments.reduce(
			(list, attachment) =>
				removedAttachmentIds.has(attachment.id)
					? list
					: addAttachment(list, attachment),
			visible,
		);
	}, [
		attachments,
		attachmentQuery.data,
		optimisticAttachments,
		removedAttachmentIds,
	]);

	const startUpload = React.useCallback(
		(file: File) => {
			if (!entryId) return;
			const localId = crypto.randomUUID();
			setUploads((u) => [
				...u,
				{ id: localId, file, status: "uploading", progress: 0 },
			]);
			upload
				.mutateAsync({
					file,
					timekeeperId,
					onProgress: (loaded, total) =>
						setUploads((u) =>
							u.map((x) =>
								x.id === localId
									? { ...x, progress: total ? loaded / total : 0 }
									: x,
							),
						),
				})
				.then((attachment) => {
					queryClient.setQueryData<Attachment[]>(
						attachmentQueryKey,
						(list = []) => addAttachment(list, attachment),
					);
					setOptimisticAttachments((list) => addAttachment(list, attachment));
					setUploads((u) => u.filter((x) => x.id !== localId));
				})
				.catch((err: unknown) =>
					setUploads((u) =>
						u.map((x) =>
							x.id === localId
								? {
										...x,
										status: "error",
										error: err instanceof Error ? err.message : "Upload failed",
									}
								: x,
						),
					),
				);
		},
		[attachmentQueryKey, entryId, queryClient, timekeeperId, upload],
	);

	const handleFiles = React.useCallback(
		(picked: File[]) => {
			const valid: File[] = [];
			for (const f of picked) {
				const err = validateAttachmentFile(f);
				if (err) toast.error(err);
				else valid.push(f);
			}
			if (!valid.length) return;
			if (entryId) valid.forEach(startUpload);
			else onFilesChange?.([...(files ?? []), ...valid]);
		},
		[entryId, files, onFilesChange, startUpload],
	);

	const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = Array.from(e.target.files ?? []);
		e.target.value = "";
		handleFiles(picked);
	};

	const removeExisting = (att: Attachment) => {
		if (!entryId) return;
		setRemovedAttachmentIds((ids) => new Set(ids).add(att.id));
		queryClient.setQueryData<Attachment[]>(attachmentQueryKey, (list = []) =>
			list.filter((attachment) => attachment.id !== att.id),
		);
		remove.mutate(
			{ attachmentId: att.id, timekeeperId },
			{
				onError: () => {
					setRemovedAttachmentIds((ids) => {
						const next = new Set(ids);
						next.delete(att.id);
						return next;
					});
					void queryClient.invalidateQueries({ queryKey: attachmentQueryKey });
				},
			},
		);
	};

	const download = async (att: Attachment) => {
		const resp = await makeApiRequest({
			endpoint: `/v2/entries/${entryId}/attachments/${att.id}`,
			method: "GET",
			headers: actingHeaders(timekeeperId),
		});
		if (resp.status === "ERROR" || !resp.data?.downloadUrl) {
			toast.error(resp.error || "Failed to get download link");
			return;
		}
		window.open(resp.data.downloadUrl, "_blank", "noopener");
	};

	const retry = (upload: Upload) => {
		setUploads((u) => u.filter((x) => x.id !== upload.id));
		startUpload(upload.file);
	};

	const isLazyLoading = attachments === undefined && attachmentQuery.isLoading;
	const lazyLoadError =
		attachments === undefined &&
		attachmentQuery.data === undefined &&
		attachmentQuery.error instanceof Error
			? attachmentQuery.error.message
			: null;
	const hasAny =
		existing.length > 0 || uploads.length > 0 || (files?.length ?? 0) > 0;

	return (
		<div className="grid w-full gap-1.5">
			<div className="flex items-center justify-between">
				<Label>Attachments</Label>
				{!disabled && (
					<>
						<input
							ref={inputRef}
							type="file"
							multiple
							accept={ATTACHMENT_ALLOWED_TYPES.join(",")}
							className="hidden"
							onChange={onPick}
						/>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="h-7 gap-1.5 px-2 text-xs"
							onClick={() => inputRef.current?.click()}
						>
							<Paperclip className="size-3.5" />
							Add files
						</Button>
					</>
				)}
			</div>

			{isLazyLoading && (
				<Row
					icon={<Loader2 className="size-4 animate-spin" />}
					name="Loading attachments…"
				/>
			)}

			{lazyLoadError && (
				<Row
					icon={<AlertCircle className="size-4 text-destructive" />}
					name="Failed to load attachments"
					meta={lazyLoadError}
					actions={
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="size-7"
							aria-label="Retry loading attachments"
							onClick={() => attachmentQuery.refetch()}
						>
							<RotateCw className="size-4" />
						</Button>
					}
				/>
			)}

			{hasAny && (
				<div className="grid max-h-40 gap-1 overflow-y-auto">
					{/* Saved attachments (edit mode) */}
					{existing.map((att) => (
						<Row
							key={att.id}
							icon={<FileText className="size-4" />}
							name={att.fileName}
							meta={
								att.status === "pending" ? "Uploading…" : formatBytes(att.size)
							}
							actions={
								<>
									{att.status === "uploaded" && (
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="size-7"
											aria-label="Download"
											onClick={() => download(att)}
										>
											<Download className="size-4" />
										</Button>
									)}
									{!disabled && (
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="size-7 text-muted-foreground hover:text-destructive"
											aria-label="Remove"
											onClick={() => removeExisting(att)}
										>
											<X className="size-4" />
										</Button>
									)}
								</>
							}
						/>
					))}

					{/* In-flight uploads (edit mode) */}
					{uploads.map((up) => (
						<Row
							key={up.id}
							icon={
								up.status === "error" ? (
									<AlertCircle className="size-4 text-destructive" />
								) : (
									<Loader2 className="size-4 animate-spin" />
								)
							}
							name={up.file.name}
							meta={
								up.status === "error"
									? up.error
									: `Uploading… ${Math.round(up.progress * 100)}%`
							}
							actions={
								up.status === "error" ? (
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="size-7"
										aria-label="Retry"
										onClick={() => retry(up)}
									>
										<RotateCw className="size-4" />
									</Button>
								) : undefined
							}
						/>
					))}

					{/* Staged files (create mode — uploaded after the entry is saved) */}
					{files?.map((file, idx) => (
						<Row
							key={`${file.name}-${file.size}-${file.lastModified}`}
							icon={<FileText className="size-4" />}
							name={file.name}
							meta={formatBytes(file.size)}
							actions={
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-7 text-muted-foreground hover:text-destructive"
									aria-label="Remove"
									onClick={() =>
										onFilesChange?.(files.filter((_, i) => i !== idx))
									}
								>
									<X className="size-4" />
								</Button>
							}
						/>
					))}
				</div>
			)}
		</div>
	);
}
